import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API, useAuth, formatErr } from "@/context/AuthContext";
import { useSites } from "@/context/SiteContext";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  FilePdf, Users, TreeStructure, ChartBar, CaretUp, Star, Buildings,
  Toolbox, ArrowsClockwise,
} from "@phosphor-icons/react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, CartesianGrid, ComposedChart, Legend, Cell,
} from "recharts";

export default function HRReport() {
  const { user } = useAuth();
  const { sites } = useSites();
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [siteId, setSiteId] = useState("all");
  const [granularity, setGranularity] = useState("week");
  const [chartType, setChartType] = useState("bar");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const t = new Date();
    setEnd(t.toISOString().slice(0, 10));
    const first = new Date(t.getFullYear(), t.getMonth(), 1);
    setStart(first.toISOString().slice(0, 10));
  }, []);

  const load = async () => {
    if (!start || !end) return;
    setLoading(true);
    try {
      const params = { start, end, granularity };
      if (siteId !== "all") params.site_id = siteId;
      const { data } = await axios.get(`${API}/reports/hr/summary`, { params });
      setData(data);
    } catch (e) { toast.error(formatErr(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [start, end, siteId, granularity]); // eslint-disable-line

  const downloadPdf = async () => {
    try {
      const params = { start, end, granularity };
      if (siteId !== "all") params.site_id = siteId;
      const res = await axios.get(`${API}/reports/hr/summary.pdf`,
        { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      const suffix = siteId === "all" ? "tum-santiyeler" : (sites.find((s) => s.id === siteId)?.code || "santiye");
      a.href = url; a.download = `personel-raporu-${suffix}-${start}-${end}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF indirildi");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const activeSites = useMemo(
    () => sites.filter((s) => s.status === "aktif"),
    [sites],
  );

  const siteBlocks = data?.sites || [];
  const topKpi = data?.kpi || {};

  return (
    <Shell
      title="Personel Yönetimi Raporu"
      actions={
        <Button onClick={downloadPdf} data-testid="hr-report-pdf-btn"
                className="rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153859] text-white">
          <FilePdf size={16} className="mr-2" /> PDF Olarak İndir
        </Button>
      }
    >
      {/* Filtre çubuğu */}
      <Card className="p-4 rounded-none sm:rounded-sm border-slate-200 shadow-sm mb-4 flex flex-wrap gap-3 items-end">
        <div>
          <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600 flex items-center gap-1">
            <Buildings size={11} weight="fill" /> Şantiye
          </Label>
          <Select value={siteId} onValueChange={setSiteId}>
            <SelectTrigger className="mt-1 rounded-none sm:rounded-sm w-64" data-testid="hr-report-site">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Şantiyeler</SelectItem>
              {activeSites.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  <span className="font-mono text-xs mr-2 text-slate-500">{s.code}</span>{s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Başlangıç</Label>
          <Input type="date" value={start} data-testid="hr-report-start"
                 onChange={(e) => setStart(e.target.value)}
                 className="mt-1 rounded-none sm:rounded-sm w-44" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Bitiş</Label>
          <Input type="date" value={end} data-testid="hr-report-end"
                 onChange={(e) => setEnd(e.target.value)}
                 className="mt-1 rounded-none sm:rounded-sm w-44" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Kırılım</Label>
          <Select value={granularity} onValueChange={setGranularity}>
            <SelectTrigger className="mt-1 rounded-none sm:rounded-sm w-32" data-testid="hr-report-granularity">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="day">Gün</SelectItem>
              <SelectItem value="week">Hafta</SelectItem>
              <SelectItem value="month">Ay</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 text-xs text-slate-500 self-end pb-2">
          {siteId === "all"
            ? "Rapor tüm aktif şantiyeler için ayrı sayfalar halinde hazırlanır."
            : "Bu şantiye için ekip kırılımlı özet hazırlanır."}
        </div>
      </Card>

      {/* Toplam / birleştirilmiş KPI */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <Kpi label={siteId === "all" ? "TOPLAM (Tüm Şantiyeler)" : "Toplam Personel"}
             value={topKpi.total} icon={<Users size={20} weight="duotone" />} />
        <Kpi label="Aktif" value={topKpi.active} tone="emerald" />
        <Kpi label="Emekli" value={topKpi.retired} tone="purple" />
        <Kpi label="Pasif" value={topKpi.passive} tone="muted" />
        <Kpi label="Bu Dönem İşe Giriş" value={topKpi.hires_in_range} tone="brand" icon={<CaretUp size={20} weight="duotone" />} />
        <Kpi label="Bu Dönem Çıkış" value={topKpi.terminations_in_range} tone="red" />
        <Kpi label="Ort. Yaş" value={topKpi.avg_age} />
        <Kpi label="Ort. Kıdem (yıl)" value={topKpi.avg_tenure_years} />
      </div>

      {/* Site blocks */}
      {siteBlocks.length === 0 && (
        <Card className="p-8 rounded-none sm:rounded-sm border-slate-200 shadow-sm text-center text-slate-500">
          Bu tarih aralığında personel kaydı yok.
        </Card>
      )}
      {siteBlocks.map((block, si) => (
        <SiteBlock key={block.site_id || si}
                   block={block}
                   granularity={granularity}
                   chartType={chartType}
                   setChartType={setChartType} />
      ))}

      {loading && <div className="text-center text-slate-500 text-sm mt-4">Yükleniyor...</div>}
    </Shell>
  );
}

const SiteBlock = ({ block, granularity, chartType, setChartType }) => {
  const kpi = block.kpi || {};
  const teams = block.teams || [];
  const movement = block.movement_trend || [];
  const teamDist = block.team_distribution || [];
  const posDist = block.position_distribution || [];
  const gLabel = { day: "Günlük", week: "Haftalık", month: "Aylık" }[granularity] || granularity;
  return (
    <Card className="p-5 rounded-none sm:rounded-sm border-slate-200 shadow-sm mb-4" data-testid={`hr-site-block-${block.site_id}`}>
      <div className="flex items-center gap-2 mb-4 pb-3 border-b border-slate-200">
        <Buildings size={20} weight="duotone" className="text-[#1B4776]" />
        <div className="text-lg font-bold font-heading text-[#1B4776]">
          {block.site_name}
        </div>
      </div>

      {/* Şantiye KPI mini ızgarası */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
        <MiniKpi label="Toplam" value={kpi.total} />
        <MiniKpi label="Aktif" value={kpi.active} tone="emerald" />
        <MiniKpi label="Emekli" value={kpi.retired} tone="purple" />
        <MiniKpi label="Bu D. Giriş" value={kpi.hires_in_range} tone="brand" />
        <MiniKpi label="Bu D. Çıkış" value={kpi.terminations_in_range} tone="red" />
        <MiniKpi label="Pasif" value={kpi.passive} tone="muted" />
        <MiniKpi label="Ort. Yaş" value={kpi.avg_age} />
        <MiniKpi label="Ort. Kıdem" value={kpi.avg_tenure_years} />
      </div>

      {/* Personel Hareket Trendi */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <ArrowsClockwise size={14} weight="duotone" className="text-[#1B4776]" />
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-700">
              Personel Hareket Trendi — {gLabel}
            </div>
          </div>
          <div className="flex items-center gap-0">
            <button onClick={() => setChartType("bar")}
                    data-testid="hr-chart-type-bar"
                    className={`px-2 py-1 text-[10px] uppercase tracking-widest font-bold border ${chartType === "bar" ? "bg-[#1B4776] text-white border-[#1B4776]" : "border-slate-300 text-slate-600 hover:bg-slate-100"}`}>
              Bar + Çizgi
            </button>
            <button onClick={() => setChartType("line")}
                    data-testid="hr-chart-type-line"
                    className={`px-2 py-1 text-[10px] uppercase tracking-widest font-bold border ${chartType === "line" ? "bg-[#1B4776] text-white border-[#1B4776]" : "border-slate-300 text-slate-600 hover:bg-slate-100"}`}>
              Sadece Aktif
            </button>
          </div>
        </div>
        <div className="w-full h-60" data-testid={`hr-chart-trend-${block.site_id}`}>
          {movement.length === 0 ? (
            <div className="h-full flex items-center justify-center text-xs text-slate-400">
              Bu dönemde personel hareketi yok.
            </div>
          ) : (
            <ResponsiveContainer>
              {chartType === "bar" ? (
                <ComposedChart data={movement} margin={{ left: 0, right: 20, top: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="period" tick={{ fontSize: 10, fill: "#334155" }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 10, fill: "#475569" }} allowDecimals={false} />
                  <YAxis yAxisId="right" orientation="right"
                         tick={{ fontSize: 10, fill: "#556B10" }} allowDecimals={false} />
                  <Tooltip contentStyle={{ borderRadius: 0, borderColor: "#CBD5E1", fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} iconSize={10} />
                  <Bar yAxisId="left" dataKey="hires" name="Giren"
                       fill="#1B4776" radius={[2, 2, 0, 0]} minPointSize={1} />
                  <Bar yAxisId="left" dataKey="terminations" name="Çıkan"
                       fill="#B91C1C" radius={[2, 2, 0, 0]} minPointSize={1} />
                  <Line yAxisId="right" type="monotone" dataKey="active" name="Aktif Toplam"
                        stroke="#556B10" strokeWidth={2}
                        dot={{ fill: "#95C11F", r: 3 }} />
                </ComposedChart>
              ) : (
                <LineChart data={movement} margin={{ left: 0, right: 20, top: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="period" tick={{ fontSize: 10, fill: "#334155" }} />
                  <YAxis tick={{ fontSize: 10, fill: "#475569" }} allowDecimals={false} />
                  <Tooltip contentStyle={{ borderRadius: 0, borderColor: "#CBD5E1", fontSize: 12 }}
                           formatter={(v) => [`${v} kişi`, "Aktif"]} />
                  <Line type="monotone" dataKey="active" name="Aktif Toplam"
                        stroke="#556B10" strokeWidth={2}
                        dot={{ fill: "#95C11F", r: 3 }} />
                </LineChart>
              )}
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Ekip Dağılımı + Meslek Kodu Dağılımı — Yatay Barlar */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <DistributionChart
          testId={`hr-team-dist-${block.site_id}`}
          title="Ekiplere Göre Personel Dağılımı"
          icon={<TreeStructure size={14} weight="duotone" className="text-[#1B4776]" />}
          data={teamDist.map((t) => ({
            label: `[${t.team_code || "--"}] ${t.team_name}`,
            value: t.active,
          }))}
          color="#1B4776"
          emptyLabel="Ekipli personel yok."
        />
        <DistributionChart
          testId={`hr-pos-dist-${block.site_id}`}
          title="Meslek Kodu'na Göre Personel Dağılımı"
          icon={<Toolbox size={14} weight="duotone" className="text-[#556B10]" />}
          data={posDist.map((d) => ({ label: d.label, value: d.count }))}
          color="#95C11F"
          textColor="#556B10"
          emptyLabel="Meslek kodu tanımlı aktif personel yok."
        />
      </div>

      {/* Ekipler detay */}
      <div className="mb-1">
        <div className="flex items-center gap-2 mb-2">
          <TreeStructure size={14} weight="duotone" className="text-[#1B4776]" />
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-700">
            Ekiplere Göre Detay
          </div>
        </div>
        {teams.length === 0 ? (
          <div className="text-xs text-slate-400 text-center py-6">
            Bu şantiyede ekipli personel yok.
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
            {teams.map((t) => (
              <TeamCard key={t.team_id || t.team_name} team={t} />
            ))}
          </div>
        )}
      </div>
    </Card>
  );
};

const DistributionChart = ({ testId, title, icon, data, color, textColor, emptyLabel, labelWidth = 180 }) => {
  const height = Math.max(120, Math.min(500, data.length * 28 + 20));
  return (
    <div data-testid={testId} className="border border-slate-200 bg-slate-50 p-3">
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-700">
          {title}
        </div>
      </div>
      {data.length === 0 ? (
        <div className="text-xs text-slate-400 text-center py-6">{emptyLabel}</div>
      ) : (
        <ResponsiveContainer width="100%" height={height}>
          <BarChart data={data} layout="vertical"
                    margin={{ left: 0, right: 32, top: 4, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 10, fill: "#475569" }} allowDecimals={false} />
            <YAxis type="category" dataKey="label" width={labelWidth}
                   tick={{ fontSize: 10, fill: "#0F172A" }} interval={0} />
            <Tooltip contentStyle={{ borderRadius: 0, borderColor: "#CBD5E1", fontSize: 12 }}
                     formatter={(v) => [`${v} kişi`, "Aktif"]} />
            <Bar dataKey="value" fill={color} radius={[0, 3, 3, 0]}
                 label={{ position: "right",
                          fill: textColor || color,
                          fontSize: 10,
                          fontWeight: 700 }} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

const TeamCard = ({ team }) => (
  <div className="border border-slate-200 bg-slate-50 p-3 rounded-none sm:rounded-sm" data-testid={`hr-team-card-${team.team_id}`}>
    <div className="flex items-center gap-2 mb-2">
      {team.team_code && (
        <span className="font-mono text-[10px] font-bold text-white bg-[#1B4776] px-1.5 py-0.5">
          {team.team_code}
        </span>
      )}
      <div className="flex-1">
        <div className="font-medium text-sm text-slate-800">{team.team_name}</div>
        {team.team_duty && (
          <div className="text-[10px] text-slate-500">{team.team_duty}</div>
        )}
      </div>
      {team.leader_name && team.leader_name !== "-" && (
        <div className="flex items-center gap-1 text-[10px] text-[#556B10] font-bold">
          <Star size={10} weight="fill" /> {team.leader_name}
        </div>
      )}
    </div>
    <div className="grid grid-cols-4 gap-1 text-center">
      <MiniStat label="Toplam" value={team.kpi?.total} />
      <MiniStat label="Aktif" value={team.kpi?.active} tone="emerald" />
      <MiniStat label="Emekli" value={team.kpi?.retired} tone="purple" />
      <MiniStat label="Giriş" value={team.kpi?.hires_in_range} tone="brand" />
      <MiniStat label="Çıkış" value={team.kpi?.terminations_in_range} tone="red" />
      <MiniStat label="Ort. Yaş" value={team.kpi?.avg_age} />
      <MiniStat label="Kıdem" value={team.kpi?.avg_tenure_years} />
      <MiniStat label="Pasif" value={team.kpi?.passive} tone="muted" />
    </div>
  </div>
);

const Kpi = ({ label, value, tone = "slate", icon }) => {
  const toneClass = {
    slate: "text-slate-900", emerald: "text-emerald-700",
    muted: "text-slate-500", purple: "text-purple-700",
    brand: "text-[#1B4776]", red: "text-red-700",
  }[tone];
  return (
    <Card className="p-4 rounded-none sm:rounded-sm border-slate-200 shadow-sm">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{label}</div>
        {icon && <span className="text-slate-300">{icon}</span>}
      </div>
      <div className={`text-2xl font-bold font-heading ${toneClass}`}>{value ?? 0}</div>
    </Card>
  );
};

const MiniKpi = ({ label, value, tone = "slate" }) => {
  const toneClass = {
    slate: "text-slate-900", emerald: "text-emerald-700",
    muted: "text-slate-500", purple: "text-purple-700",
    brand: "text-[#1B4776]", red: "text-red-700",
  }[tone];
  return (
    <div className="border border-slate-200 bg-white p-2">
      <div className="text-[9px] uppercase tracking-widest font-bold text-slate-500">{label}</div>
      <div className={`text-lg font-bold ${toneClass}`}>{value ?? 0}</div>
    </div>
  );
};

const MiniStat = ({ label, value, tone = "slate" }) => {
  const toneClass = {
    slate: "text-slate-800", emerald: "text-emerald-700",
    muted: "text-slate-400", purple: "text-purple-700",
    brand: "text-[#1B4776]", red: "text-red-700",
  }[tone];
  return (
    <div className="border border-slate-100 bg-white px-1 py-1">
      <div className="text-[8px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`text-xs font-bold ${toneClass}`}>{value ?? 0}</div>
    </div>
  );
};
